import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import { Star, ArrowLeft, CircleNotch, Target, CaretUp, CaretDown } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ExportButton } from '../components/ExportCSV';
import WindowNarrative from '../components/WindowNarrative';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BALL_KEYS = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

const PhaseBadge = ({ phase }) => {
  const cls = { SPIKE: 'bg-red-100 text-red-800', ACTIVE: 'bg-amber-100 text-amber-800', DORMANT: 'bg-blue-100 text-blue-800', NORMAL: 'bg-gray-100 text-gray-600' };
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${cls[phase] || cls.NORMAL}`}>{phase}</span>;
};

/* ================================================================
 * W27 Introduction — What this window analyses
 * ================================================================ */
function buildW27Intro(data) {
  const total = data?.total_draws;
  return {
    testid: 'w27-introduction',
    body: (
      <>
        Window 27 is the <strong>Outlier / Spike-Dormancy Detector</strong>. For every number 1–50 at each of the 5 ball positions, it compares how often that number has actually appeared <em>recently</em> against how often it <em>should</em> have appeared based on its own long-run historical rate — flagging numbers that are currently behaving unusually hot or unusually cold relative to their own history.
      </>
    ),
    bullets: [
      <>
        <strong>W10 / W20 / W50</strong> (table columns): three independent <strong>rolling lookback windows</strong> — the last 10, 20, and 50 draws respectively. For each, we count the number&apos;s actual appearances in that window and its statistically <strong>expected</strong> count (= overall historical rate × window size). This gives a short (W10), medium (W20), and long (W50) lens on recent behaviour. <em>Note: these are just lookback sizes local to this window — unrelated to other pages in this app named &quot;Window 50&quot; etc.</em>
      </>,
      <>
        <strong>Max Z / Min Z</strong>: each W10/W20/W50 reading is converted to a <strong>z-score</strong> — <code className="bg-white/60 px-1 rounded">z = (actual count − expected count) ÷ √(expected count)</code> — the standard way to measure how many standard deviations a count deviates from what pure chance would predict. <strong>Max Z</strong> is the highest (most positive) of the 3 z-scores — a strong positive value means the number has appeared far <em>more</em> than expected recently (a hot spike). <strong>Min Z</strong> is the lowest (most negative) — a strong negative value means the number has appeared far <em>less</em> than expected (gone cold / dormant).
      </>,
      <>
        <strong>Phase</strong>: <span className="font-bold text-red-700">SPIKE</span> (Max Z &gt; 2, a statistically strong hot streak) · <span className="font-bold text-amber-700">ACTIVE</span> (Max Z &gt; 1, mildly hot) · <span className="font-bold text-blue-700">DORMANT</span> (Min Z &lt; -1, running cold) · <span className="font-bold text-gray-600">NORMAL</span> (behaving as its own history would predict).
      </>,
      <>
        <strong>Score</strong> (prediction score): a weighted blend — <code className="bg-white/60 px-1 rounded">30% overall historical frequency + 20% dormant mean-reversion (bets cold numbers are &quot;due&quot;) + 20% active/spike momentum (bets hot numbers keep hitting) + 15% dormant bonus + 15% active bonus</code>. It deliberately rewards numbers at <em>either</em> extreme (very hot or very cold) over numbers sitting quietly at NORMAL, while still anchoring on raw frequency as the base.
      </>,
      <>
        <strong>Data window</strong>: computed across all {total ? total.toLocaleString() : '—'} historical draws. Recalculates automatically whenever a new draw is entered.
      </>,
    ],
  };
}

/* ================================================================
 * W27 Observations — Key findings (auto-computed across the 5 balls)
 * ================================================================ */
function buildW27Observations(data) {
  const balls = data?.balls || {};
  const perBall = BALL_KEYS.map((bk) => {
    const nums = balls[bk] || [];
    if (!nums.length) return null;
    const spikes = nums.filter((n) => n.phase === 'SPIKE');
    const active = nums.filter((n) => n.phase === 'ACTIVE');
    const dormant = nums.filter((n) => n.phase === 'DORMANT');
    const topHot = [...nums].sort((a, z) => (z.max_z || 0) - (a.max_z || 0))[0];
    const topCold = [...nums].sort((a, z) => (a.min_z || 0) - (z.min_z || 0))[0];
    return { pos: bk.replace('ball_', ''), spikes, active, dormant, topHot, topCold };
  }).filter(Boolean);

  const totalSpikes = perBall.reduce((s, p) => s + p.spikes.length, 0);
  const totalDormant = perBall.reduce((s, p) => s + p.dormant.length, 0);
  const hottestOverall = [...perBall].sort((a, z) => (z.topHot?.max_z || 0) - (a.topHot?.max_z || 0))[0];
  const coldestOverall = [...perBall].sort((a, z) => (a.topCold?.min_z || 0) - (z.topCold?.min_z || 0))[0];

  return {
    testid: 'w27-observations',
    body: 'Auto-generated summary of the current hot/cold landscape across the 5 ball positions.',
    bullets: [
      {
        content: <><strong>{totalSpikes} SPIKE number{totalSpikes === 1 ? '' : 's'}</strong> (Max Z &gt; 2) and <strong>{totalDormant} DORMANT</strong> (Min Z &lt; -1) detected across all 5 balls right now.</>,
      },
      hottestOverall?.topHot && {
        content: <><strong>Hottest right now</strong>: Ball {hottestOverall.pos} — number <strong>{hottestOverall.topHot.number}</strong> has a Max Z of <strong>{hottestOverall.topHot.max_z}</strong> (phase: <PhaseBadge phase={hottestOverall.topHot.phase} />).</>,
      },
      coldestOverall?.topCold && {
        content: <><strong>Coldest right now</strong>: Ball {coldestOverall.pos} — number <strong>{coldestOverall.topCold.number}</strong> has a Min Z of <strong>{coldestOverall.topCold.min_z}</strong> (phase: <PhaseBadge phase={coldestOverall.topCold.phase} />).</>,
      },
      ...perBall.map((p) => ({
        content: <><strong>Ball {p.pos}</strong>: {p.spikes.length} spike, {p.active.length} active, {p.dormant.length} dormant · hottest = <strong>#{p.topHot?.number}</strong> (z:{p.topHot?.max_z}) · coldest = <strong>#{p.topCold?.number}</strong> (z:{p.topCold?.min_z}).</>,
      })),
    ].filter(Boolean),
    footer: 'A SPIKE or DORMANT tag alone is not a guarantee — the Score column (see Introduction) blends this signal with raw historical frequency.',
  };
}

/* ================================================================
 * W27 Key Findings — applicable to next draw
 * ================================================================ */
function buildW27KeyFindings(data) {
  const balls = data?.balls || {};
  const picks = BALL_KEYS.map((bk) => {
    const nums = balls[bk] || [];
    if (!nums.length) return null;
    const top = [...nums].sort((a, z) => (z.pred_score || 0) - (a.pred_score || 0))[0];
    if (!top) return null;
    return { pos: bk.replace('ball_', ''), top };
  }).filter(Boolean);

  const ticket = picks.map((p) => p.top.number).sort((a, b) => a - b);

  return {
    testid: 'w27-key-findings',
    title: 'Key Findings — applicable to next draw',
    body: 'Top pick per ball under the Score formula above, followed by the composed ticket.',
    badge: ticket.length === 5 ? { color: 'from-indigo-600 to-blue-700', icon: '🎯', label: 'W27 composed ticket', detail: ticket.join(' · ') } : null,
    bullets: picks.map((p) => ({
      content: <>
        <strong>Ball {p.pos}</strong> → top pick <span className="inline-flex w-7 h-7 rounded-full bg-indigo-600 text-white items-center justify-center font-mono font-black text-xs mx-1">{p.top.number}</span>
        <span className="text-xs text-gray-600 font-mono">
          score {p.top.pred_score} · <PhaseBadge phase={p.top.phase} /> · Max Z {p.top.max_z} · Min Z {p.top.min_z} · seen {p.top.overall}x historically
        </span>
      </>,
    })),
    footer: 'These are model-suggested candidates only; consult W53 (Ball Master) or W62 (Consolidated Verdict) for the cross-window consensus.',
  };
}

export default function Window27OutlierDetection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'pred_score', dir: 'desc' });

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/outlier-detection`); setData(res.data); }
      catch { toast.error('Failed to fetch outlier detection'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div><h1 className="text-2xl font-bold text-gray-900">Window 27</h1><p className="text-xs text-gray-500">Outlier Detection</p></div>
            </div>
            <ExportButton data={data} filename="w27_outlier_detection" />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div> : data ? (
          <>
            <div className="mb-6">
              <WindowNarrative windowId="W27" intro={buildW27Intro(data)} observations={buildW27Observations(data)} keyFindings={buildW27KeyFindings(data)} />
            </div>
            <Tabs defaultValue="ball_1" className="space-y-4">
            <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
              {Object.entries(ballLabels).map(([k, l]) => (
                <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${k}`}>{l}</TabsTrigger>
              ))}
            </TabsList>
            {Object.entries(ballLabels).map(([key, label]) => {
              const nums = data.balls?.[key] || [];
              const spikes = nums.filter(n => n.phase === 'SPIKE').length;
              const dormant = nums.filter(n => n.phase === 'DORMANT').length;
              const sorted = [...nums].sort((a, b) => {
                let aV = a[sortConfig.key] || 0, bV = b[sortConfig.key] || 0;
                return sortConfig.dir === 'desc' ? bV - aV : aV - bV;
              });
              const topPredicted = new Set([...nums].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
              return (
                <TabsContent key={key} value={key}>
                  <div className="flex gap-3 mb-4 text-xs font-bold">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded">SPIKES: {spikes}</span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">DORMANT: {dormant}</span>
                  </div>
                  <div className="bg-white border rounded overflow-hidden">
                    <div className="overflow-x-auto" data-testid="outlier-table">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            {[['number','#'],['overall','All'],['overall_pct','%'],['max_z','Max Z'],['min_z','Min Z'],['pred_score','Score']].map(([k,l]) => (
                              <th key={k} className="px-2 py-2 text-[10px] uppercase cursor-pointer hover:bg-gray-200 text-center" onClick={() => handleSort(k)}>{l} <SortIcon k={k} /></th>
                            ))}
                            <th className="px-2 py-2 text-[10px] uppercase text-center">W10</th>
                            <th className="px-2 py-2 text-[10px] uppercase text-center">W20</th>
                            <th className="px-2 py-2 text-[10px] uppercase text-center">W50</th>
                            <th className="px-2 py-2 text-[10px] uppercase text-center">Phase</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((n, idx) => (
                            <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={topPredicted.has(n.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`outlier-row-${n.number}`}>
                              <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                              <td className="px-2 py-1 border-b font-mono text-center">{n.overall}</td>
                              <td className="px-2 py-1 border-b font-mono text-center text-[10px]">{n.overall_pct}%</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.max_z > 2 ? 'text-red-600' : ''}`}>{n.max_z}</td>
                              <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.min_z < -1 ? 'text-blue-600' : ''}`}>{n.min_z}</td>
                              <td className="px-2 py-1 border-b font-mono text-center font-bold text-sm">{n.pred_score}</td>
                              {n.spikes.map((sp, i) => (
                                <td key={i} className={`px-2 py-1 border-b font-mono text-center text-[10px] ${sp.z_score > 2 ? 'text-red-600 font-bold' : sp.z_score < -1 ? 'text-blue-600' : ''}`}>{sp.count} (z:{sp.z_score})</td>
                              ))}
                              <td className="px-2 py-1 border-b text-center"><PhaseBadge phase={n.phase} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                    <div className="flex items-center gap-2 mb-2"><Target size={24} weight="bold" /><h3 className="font-bold text-xl">Predicted {label} (Dormant Numbers — Mean Reversion)</h3></div>
                    <p className="text-xs text-white/70 mb-4">Dormant numbers with strong historical frequency get boosted</p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {[...nums].sort((a, b) => b.pred_score - a.pred_score).slice(0, 10).map((n, idx) => (
                        <div key={n.number} className={`p-3 rounded-lg ${idx === 0 ? 'bg-sky-100 text-sky-900' : 'bg-white/10'}`} data-testid={`pred-${n.number}`}>
                          <div className="text-2xl font-mono font-bold text-center">{n.number}</div>
                          <div className="text-xs text-center mt-1 opacity-80"><PhaseBadge phase={n.phase} /></div>
                          <div className="text-center font-bold mt-1">{n.pred_score}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
          </>
        ) : null}
      </main>
    </div>
  );
}
